/* eslint-disable sort-keys */
import api from '@common/api';
import logging from '@common/logging';
import session from '@common/session';
import {
  AccreditSelfMutationVariables,
  AccreditationStatus,
  BankDetail,
  EmploymentStatus,
  InvestorAccount,
  PositionValue,
  SubscriptionOffer,
  Trade,
  AssetClass,
  RegulatoryRuleType,
} from '@services/appsync/types';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  EmploymentInformationValues,
  ExitTimingValues,
  ExperienceValues,
  FinanceInformationValues,
  ObjectivesValues,
  SelfAccreditationClaims,
} from './types';

export interface InvestorModelProps {
  accountValue: PositionValue | null;
  investorAccount: InvestorAccount | null;
  subscriptionOffer: SubscriptionOffer | null; // Cash Flow subscription offer
  sfrEligibility?: {
    eligible: boolean;
    needsBankAccount: boolean;
    needsToFillOutExperienceSurvey: boolean;
    needsToFillOutObjectivesSurvey: boolean;
    needsToFillOutIdentity: boolean;
    needsToFillOutNetWorth: boolean;
    needsToFillOutAnnualIncome: boolean;
  };
  cashflowEligibility?: {
    eligible: boolean;
    needsBankAccount: boolean;
    needsToFillOutExperienceSurvey: boolean;
    needsToFillOutObjectivesSurvey: boolean;
    needsToFillOutIdentity: boolean;
    needsToFillOutNetWorth: boolean;
    needsToFillOutAnnualIncome: boolean;
  };
  cashFlowOffers: SubscriptionOffer[];
  selectedCashFlowCampaignId?: string;
}

export interface InvestorModelActions {
  fetchAccountValue: () => Promise<{ error?: string; success: boolean; accountValue?: PositionValue }>;
  fetchInvestorAccount: () => Promise<{ success: boolean; error?: string; investorAccount?: InvestorAccount | null }>;
  checkCashflowEligibility: ({ bankAccounts }: { bankAccounts: BankDetail[] }) => Promise<{
    eligible: boolean;
    needsBankAccount: boolean;
    needsToFillOutExperienceSurvey: boolean;
    needsToFillOutObjectivesSurvey: boolean;
    needsToFillOutIdentity: boolean;
    needsToFillOutNetWorth: boolean;
    needsToFillOutAnnualIncome: boolean;
  }>;
  checkSFREligibility: ({ bankAccounts }: { bankAccounts: BankDetail[] }) => Promise<{ eligible: boolean }>;
  isAccreditedInvestor: () => boolean;
  isFirstTimeInvestor: (trades?: Trade[]) => boolean;
  saveDOBandSSN: (dob: string, ssn: string) => Promise<{ error?: string; success: boolean }>;
  setAccountValue: (value: PositionValue | null) => void;
  setInvestorAccount: (investorAccount: InvestorAccount | null) => void;
  setSubscriptionOffer: (subscriptionOffer: SubscriptionOffer | null) => void;
  updateAccreditationInformation: (
    accreditations: SelfAccreditationClaims,
  ) => Promise<{ success: boolean; error?: string }>;
  updateAnnualIncome: (income: number) => Promise<{ error?: string; success: boolean }>;
  updateBrokerDealerAssociation: (isAssociatedWithBroker: boolean) => Promise<{ error?: string; success: boolean }>;
  updateEmploymentInformation: (data: EmploymentInformationValues) => Promise<{ success: boolean; error?: string }>;
  updateExperience: (experience: ExperienceValues) => Promise<{ error?: string; success: boolean }>;
  updateFinanceInformation: (data: FinanceInformationValues) => Promise<{ success: boolean; error?: string }>;
  updateNetWorth: (worth: number) => Promise<{ error?: string; success: boolean }>;
  updateObjectivesRisks: (data: ObjectivesValues) => Promise<{ success: boolean; error?: string }>;
  updateTimeHorizon: (data: ExitTimingValues) => Promise<{ success: boolean; error?: string }>;
  fetchCashFlowOffers: () => Promise<void>;
  setSelectedCashFlowCampaignId: (offerId: string) => void;
}

export const InvestorAccountModel = createStore(
  persist<InvestorModelProps & InvestorModelActions>(
    (set, get) => ({
      accountValue: null,
      checkCashflowEligibility: async ({ bankAccounts }) => {
        const investorAccount = get().investorAccount;

        // Check Bank Accounts
        const needsBankAccount = bankAccounts.length === 0;

        // Check Experience
        const experience = investorAccount?.non_accredited_survey_v2?.experience;
        const needsToFillOutExperienceSurvey =
          !experience ||
          experience.experience_alts == null ||
          experience?.experience_bonds == null ||
          experience?.experience_re == null ||
          experience?.experience_stocks == null;

        // Check Objectives
        const objective = investorAccount?.non_accredited_survey_v2?.objective;
        const riskProfile = investorAccount?.non_accredited_survey_v2?.risk_profile;
        const needsToFillOutObjectivesSurvey =
          !objective ||
          !Object.values(objective).every((obj) => obj != null) ||
          !riskProfile ||
          !Object.values(riskProfile).every((obj) => obj != null);

        // Check Identity
        const needsToFillOutIdentity = !investorAccount?.has_bd_account;

        // Check Net Worth
        const needsToFillOutNetWorth = investorAccount?.non_accredited_survey_v2?.net_worth?.value === null;

        // Check Annual Income
        const needsToFillOutAnnualIncome = investorAccount?.non_accredited_survey_v2?.annual_income?.value === null;

        // Check if all requirements are met
        const eligible = !(
          needsBankAccount ||
          needsToFillOutExperienceSurvey ||
          needsToFillOutObjectivesSurvey ||
          needsToFillOutIdentity ||
          needsToFillOutNetWorth ||
          needsToFillOutAnnualIncome
        );

        // Create result object
        const cashflowEligibility = {
          eligible,
          needsBankAccount,
          needsToFillOutAnnualIncome,
          needsToFillOutExperienceSurvey,
          needsToFillOutIdentity,
          needsToFillOutNetWorth,
          needsToFillOutObjectivesSurvey,
        };

        set({ cashflowEligibility });
        return cashflowEligibility;
      },
      // TODO: Implement this
      checkSFREligibility: async ({ bankAccounts }) => {
        const investorAccount = get().investorAccount;

        // Check Bank Accounts
        const needsBankAccount = bankAccounts.length === 0;

        // Check Experience
        const experience = investorAccount?.non_accredited_survey_v2?.experience;
        const needsToFillOutExperienceSurvey =
          !experience ||
          experience.experience_alts == null ||
          experience?.experience_bonds == null ||
          experience?.experience_re == null ||
          experience?.experience_stocks == null;

        // Check Objectives
        const objective = investorAccount?.non_accredited_survey_v2?.objective;
        const riskProfile = investorAccount?.non_accredited_survey_v2?.risk_profile;
        const needsToFillOutObjectivesSurvey =
          !objective ||
          !Object.values(objective).every((obj) => obj != null) ||
          !riskProfile ||
          !Object.values(riskProfile).every((obj) => obj != null);

        // Check Identity
        const needsToFillOutIdentity = !investorAccount?.has_bd_account;

        // Check Net Worth
        const needsToFillOutNetWorth = investorAccount?.non_accredited_survey_v2?.net_worth?.value === null;

        // Check Annual Income
        const needsToFillOutAnnualIncome = investorAccount?.non_accredited_survey_v2?.annual_income?.value === null;

        // Check if all requirements are met
        const eligible = !(
          needsBankAccount ||
          needsToFillOutExperienceSurvey ||
          needsToFillOutObjectivesSurvey ||
          needsToFillOutIdentity ||
          needsToFillOutNetWorth ||
          needsToFillOutAnnualIncome
        );

        // Create result object
        const sfrEligibility = {
          eligible,
          needsBankAccount,
          needsToFillOutAnnualIncome,
          needsToFillOutExperienceSurvey,
          needsToFillOutIdentity,
          needsToFillOutNetWorth,
          needsToFillOutObjectivesSurvey,
        };

        set({ sfrEligibility });
        return sfrEligibility;
      },
      fetchAccountValue: async () => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        const accountValue = await api.getAccountValue({ investor_account_id: investorID });
        if (accountValue) {
          set({ accountValue });
          return { accountValue: accountValue, success: true };
        } else {
          return { error: 'Unable to fetch account value', success: false };
        }
      },
      fetchInvestorAccount: async () => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No Investor ID from session', success: false };
        const investorAccount = await api.getInvestorAccount({ id: investorID });
        set({ investorAccount });
        return {
          investorAccount,
          success: true,
        };
      },
      investorAccount: null,
      // XXX: Do not use this any more, it needs to be replaced, ok to leave it in Cashflow for the moment
      isAccreditedInvestor: () => {
        const { investorAccount } = get();
        return investorAccount?.accreditation_self?.status === AccreditationStatus.APPROVED
          || investorAccount?.accreditation_hellosign?.status === AccreditationStatus.APPROVED
          || investorAccount?.accreditation_manual?.status === AccreditationStatus.APPROVED;
      },
      isFirstTimeInvestor: (trades) => {
        const { investorAccount } = get();
        const _trades = trades ?? [];
        const _brokerDealerAssoc = investorAccount?.non_accredited_survey_v2?.broker_dealer_assoc?.value ?? false;
        // has not trades & is not a broker dealer
        return _brokerDealerAssoc === false && _trades.length === 0;
      },
      saveDOBandSSN: async (dob: string, ssn: string) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const res = await api.createBDAccount({ dob, investor_account_id: investorID, ssn });
          if (res) {
            await get().fetchInvestorAccount();
            return { success: true };
          } else {
            return { error: 'Unable to update investor account, please try again', success: false };
          }
        } catch {
          return { error: 'Unable to update investor account, please try again', success: false };
        }
      },
      setAccountValue: (value) => {
        set({ accountValue: value });
      },
      setInvestorAccount: (investorAccount) => {
        set({ investorAccount });
      },
      setSubscriptionOffer: (subscriptionOffer) => {
        set({ subscriptionOffer: subscriptionOffer });
      },
      subscriptionOffer: null,
      updateAccreditationInformation: async (claims) => {
        const investorId = session.get()?.investorId;
        if (!investorId) throw 'Investor ID not found';
        const payload: AccreditSelfMutationVariables = claims.none
          ? { investor_account_id: investorId, is_accredited: false }
          : {
            investor_account_id: investorId,
            is_accredited: true,
            reason_annual_income: claims.income,
            reason_certification: claims.certification,
            reason_employment: claims.employment,
            reason_net_worth: claims.netWorth,
          };
        try {
          const result = await api.accreditSelf(payload);

          if (!result) {
            return { error: 'Unable to save accrediatation status', success: false };
          }
          await InvestorAccountModel.getState().fetchInvestorAccount();
          return { success: true };
        } catch {
          return { error: 'Unable to save accrediatation status', success: false };
        }
      },
      updateAnnualIncome: async (income: number) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const result = await api.updateSuitabilityV2({
            investor_account_id: investorID,
            survey: { annual_income: income },
          });
          const currentInvestor: InvestorAccount = JSON.parse(JSON.stringify(get().investorAccount));
          if (currentInvestor?.non_accredited_survey_v2 && result && result.annual_income) {
            currentInvestor.non_accredited_survey_v2.annual_income = result.annual_income;
            set({ investorAccount: currentInvestor });
          } else {
            await get().fetchInvestorAccount();
          }
          return { success: true };
        } catch {
          return { error: 'Unable to update investor account, please try again', success: false };
        }
      },
      updateBrokerDealerAssociation: async (brokerDealerAssociation) => {
        const investorId = session.get()?.investorId;
        if (!investorId) return { error: 'No investorId', success: false };

        try {
          const result = await api.updateSuitabilityV2({
            investor_account_id: investorId,
            survey: { broker_dealer_assoc: brokerDealerAssociation },
          });
          if (!result)
            return {
              error: 'Update failed',
              success: false,
            };
          await get().fetchInvestorAccount();
          return { success: true };
        } catch (err: any) {
          logging.logError(err);
          return {
            error: 'Update failed',
            success: false,
          };
        }
      },
      updateEmploymentInformation: async ({
        brokerDealerAssociation,
        employer,
        employerAddress,
        employerAddress2,
        employerCity,
        employerState,
        employerZip,
        employmentStatus = EmploymentStatus.UNEMPLOYED,
        occupation,
      }) => {
        const investorId = session.get()?.investorId;
        if (!investorId)
          return {
            error: 'No investorId',
            success: false,
          };
        try {
          // XXX: Hitting two endpoints is bad, what happens if one fails?
          // We should consolidate this into a single api call
          const updateAccountInfoResult = await api.updateInvestorAccount({
            investor_account: {
              employment: {
                employer: employer,
                employer_address: employerAddress,
                employer_address2: employerAddress2,
                employer_city: employerCity,
                employer_state: employerState,
                employer_zip: employerZip,
                employment_status: employmentStatus,
                occupation: occupation,
              },
            },
            investor_account_id: investorId,
          });

          const updateSuitabilityResult = await api.updateSuitabilityV2({
            investor_account_id: investorId,
            survey: {
              broker_dealer_assoc: brokerDealerAssociation,
            },
          });

          if (!updateAccountInfoResult || !updateSuitabilityResult)
            return {
              error: 'Update failed',
              success: false,
            };
          await get().fetchInvestorAccount();
          return { success: true };
        } catch (err: any) {
          logging.logError(err);
          return {
            error: 'Update failed',
            success: false,
          };
        }
      },
      updateExperience: async (experience) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const result = await api.updateSuitabilityV2({
            investor_account_id: investorID,
            survey: {
              experience: {
                experience_alts: experience.alternatives,
                experience_bonds: experience.bonds,
                experience_re: experience.realEstate,
                experience_stocks: experience.stocks,
              },
            },
          });
          if (result) {
            const currentInvestor: InvestorAccount = JSON.parse(JSON.stringify(get().investorAccount));
            if (currentInvestor?.non_accredited_survey_v2) {
              currentInvestor.non_accredited_survey_v2.experience = result.experience;
              set({ investorAccount: currentInvestor });
            } else {
              await get().fetchInvestorAccount();
            }
            return { success: true };
          } else {
            return { error: 'Unable to update profile', success: false };
          }
        } catch (err) {
          logging.logError(err as Error);
          return { error: 'Unable to update profile', success: false };
        }
      },
      updateFinanceInformation: async ({ annualIncome, netWorth, pct_illiquid_securities, pct_liquid_securities }) => {
        const investorId = session.get()?.investorId;
        if (!investorId) return { error: 'No investorId', success: false };

        try {
          const result = await api.updateSuitabilityV2({
            investor_account_id: investorId,
            survey: {
              annual_income: annualIncome,
              net_worth: netWorth,
              pct_illiquid_securities: pct_illiquid_securities,
              pct_liquid_securities: pct_liquid_securities,
            },
          });
          if (!result)
            return {
              error: 'Update failed',
              success: false,
            };
          await get().fetchInvestorAccount();

          return { success: true };
        } catch (err: any) {
          logging.logError(err);
          return {
            error: 'Update failed',
            success: false,
          };
        }
      },
      updateNetWorth: async (worth: number) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const response = await api.updateSuitabilityV2({
            investor_account_id: investorID,
            survey: { net_worth: worth },
          });
          const currentInvestor: InvestorAccount = JSON.parse(JSON.stringify(get().investorAccount));
          if (currentInvestor && currentInvestor.non_accredited_survey_v2 && response && response?.net_worth) {
            currentInvestor.non_accredited_survey_v2.net_worth = response.net_worth;
            set({ investorAccount: currentInvestor });
          } else {
            await get().fetchInvestorAccount();
          }
          return { success: true };
        } catch {
          return { error: 'Unable to update investor account, please try again', success: false };
        }
      },
      updateObjectivesRisks: async ({ objectiveChoice, riskProfile }) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const result = await api.updateSuitabilityV2({
            investor_account_id: investorID,
            survey: { objective: objectiveChoice, risk_profile: riskProfile },
          });
          if (result) {
            const currentInvestor: InvestorAccount = JSON.parse(JSON.stringify(get().investorAccount));
            if (currentInvestor?.non_accredited_survey_v2) {
              currentInvestor.non_accredited_survey_v2.objective = result.objective;
              currentInvestor.non_accredited_survey_v2.risk_profile = result.risk_profile;
              set({ investorAccount: currentInvestor });
            } else {
              await get().fetchInvestorAccount();
            }
            return { success: true };
          } else {
            return { error: 'Unable to update profile', success: false };
          }
        } catch (err) {
          logging.logError(err as Error);
          return { error: 'Unable to update profile', success: false };
        }
      },
      updateTimeHorizon: async ({ timeHorizon }) => {
        const investorId = session.get()?.investorId;
        if (!investorId) return { error: 'No investorId', success: false };

        try {
          const result = await api.updateSuitabilityV2({
            investor_account_id: investorId,
            survey: {
              time_horizon: timeHorizon,
            },
          });
          if (!result)
            return {
              error: 'Update failed',
              success: false,
            };
          await get().fetchInvestorAccount();
          return { success: true };
        } catch (err: any) {
          logging.logError(err);
          return {
            error: 'Update failed',
            success: false,
          };
        }
      },
      fetchCashFlowOffers: async () => {
        try {
          const offers = await api.listSubscriptionOffers({ 
            investor_account_id: session.get()?.investorId, 
            asset_class: AssetClass.MIXED,
          });
          
          if (!offers || offers.length === 0) {
            logging.logError(new Error('No subscription offers found'));
            return;
          }
          
          set({ cashFlowOffers: offers });
          
          // If no offer is selected, default to RegA for non-accredited or first RegD for accredited
          const { investorAccount } = get();
          if (!get().subscriptionOffer) {
            const isFullyAccredited = Boolean(
              investorAccount?.accreditation_manual?.status === AccreditationStatus.APPROVED ||
              investorAccount?.accreditation_hellosign?.status === AccreditationStatus.APPROVED
            );
            
            const defaultOffer = isFullyAccredited 
              ? offers.find(o => o.regulatory_rule_type === RegulatoryRuleType.REG_D_506C)
              : offers.find(o => o.regulatory_rule_type === RegulatoryRuleType.REG_A_PLUS_TIER2);
              
            if (defaultOffer) {
              set({ 
                selectedCashFlowCampaignId: defaultOffer.id,
                subscriptionOffer: defaultOffer,
              });
            }
          }
        } catch (err) {
          logging.logError(err as Error);
          throw new Error('Failed to fetch subscription offers');
        }
      },
      setSelectedCashFlowCampaignId: (offerId: string) => {
        const selectedOffer = get().cashFlowOffers.find(o => o.id === offerId);
        set({ 
          selectedCashFlowCampaignId: offerId,
          subscriptionOffer: selectedOffer ?? null,
        });
      },
      cashFlowOffers: [],
      selectedCashFlowCampaignId: undefined,
    }),
    {
      getStorage: () => localStorage,
      name: 'investor-account-model',
    },
  ),
);

export function useInvestorAccountModel(): InvestorModelProps & InvestorModelActions;
export function useInvestorAccountModel<T>(
  selector: (state: InvestorModelProps & InvestorModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useInvestorAccountModel<T>(
  selector?: (state: InvestorModelProps & InvestorModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(InvestorAccountModel, selector!, equals);
}
